import { c as _c } from "react/compiler-runtime";
import { NO_TRANSLATE_CLASS } from "@flat-ui";
import { useEffect } from "react";

/** Class ArcGIS applies to the attribution (data-source) element in the map footer. */
const ESRI_ATTRIBUTION_SELECTOR = ".esri-attribution";

/**
 * Excludes the ArcGIS map attribution (the data-source text in the map footer,
 * e.g. "Esri, TomTom, Garmin, …") from LocalizeJS translation.
 *
 * The attribution lists third-party data providers and is not app content, so it
 * must not be sent to the translation service. This marks the attribution element
 * with the `notranslate` class (and the standard `translate="no"` attribute) once
 * ArcGIS has rendered it, and re-applies it if the element is (re)created.
 *
 * @param viewRef - Ref to the ArcGIS view instance.
 * @param isViewReady - Whether the ArcGIS view has been initialized.
 */
export function useMapAttribution(viewRef, isViewReady) {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] !== isViewReady || $[1] !== viewRef) {
    t0 = () => {
      const container = viewRef.current?.container;
      if (!isViewReady || !container) {
        return;
      }
      const excludeAttributionFromTranslation = function excludeAttributionFromTranslation() {
        const attribution = container?.querySelector(ESRI_ATTRIBUTION_SELECTOR);
        if (!attribution) {
          return false;
        }
        attribution.classList.add(NO_TRANSLATE_CLASS);
        attribution.setAttribute("translate", "no");
        return true;
      };
      if (excludeAttributionFromTranslation()) {
        return;
      }
      const observer = new MutationObserver(() => {
        if (excludeAttributionFromTranslation()) {
          observer.disconnect();
        }
      });
      observer.observe(container, {
        childList: true,
        subtree: true
      });
      return () => observer.disconnect();
    };
    t1 = [isViewReady, viewRef];
    $[0] = isViewReady;
    $[1] = viewRef;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
}